import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { AppState } from 'src/store/store';
import { UserListItem, UsersState } from './types';
import usersApiService from 'src/api/services/usersApiService';
import { UserRole } from 'src/common/commonTypes';
import { notification } from 'antd';

const initialState: UsersState = {
    isLoading: false,
    items: [],
    filter: {},
    requestId: undefined
};

export const loadUsersAsync = createAsyncThunk('users/load', async () => {
    try {
        const data = await usersApiService.getUsers();
        return data;
    } catch (error) {
        if (error instanceof Error) {
            notification.error({ message: error.message });
        }
        throw new Error();
    }
});

export const lockUser = createAsyncThunk('user/lock', async (user: UserListItem) => {
    try {
        await usersApiService.lockUser(user.id);
    } catch (error) {
        if (error instanceof Error) {
            notification.error({ message: error.message });
        }
        throw new Error();
    }
});

export const unlockUser = createAsyncThunk('user/unlock', async (user: UserListItem) => {
    try {
        await usersApiService.unblockedUser(user.id);
    } catch (error) {
        if (error instanceof Error) {
            notification.error({ message: error.message });
        }
        throw new Error();
    }
});

const slice = createSlice({
    name: 'users',
    initialState,
    reducers: {
        reset: () => initialState,
        loginFilterChanged: (state, action: PayloadAction<string>) => {
            state.filter.login = action.payload;
        },
        fullNameFilterChanged: (state, action: PayloadAction<string>) => {
            state.filter.fullName = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadUsersAsync.pending, (state) => {
                state.isLoading = true;
                state.items = [];
            })
            .addCase(loadUsersAsync.fulfilled, (state, action) => {
                state.isLoading = false;
                state.items = action.payload.map((dto) => ({
                    ...dto,
                    roleName: UserRole[dto.roleName as keyof typeof UserRole]
                }));
            })
            .addCase(loadUsersAsync.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(lockUser.pending, (state, action) => {
                state.isLoading = true;
                state.requestId = action.meta.requestId;
            })
            .addCase(lockUser.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.isLoading = false;
                    const user = state.items.find((item) => item.id === action.meta.arg.id);
                    if (user) {
                        user.isActive = false;
                    }
                }
            })
            .addCase(lockUser.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.isLoading = false;
                }
            })
            .addCase(unlockUser.pending, (state, action) => {
                state.isLoading = true;
                state.requestId = action.meta.requestId;
            })
            .addCase(unlockUser.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.isLoading = false;
                    const user = state.items.find((item) => item.id === action.meta.arg.id);
                    if (user) {
                        user.isActive = true;
                    }
                }
            })
            .addCase(unlockUser.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.isLoading = false;
                }
            });
    }
});

export const { reset, loginFilterChanged, fullNameFilterChanged } = slice.actions;

const selectUsers = (state: AppState) => state.users;

export const selectIsLoading = (state: AppState) => selectUsers(state).isLoading;

const selectItems = (state: AppState) => selectUsers(state).items;

export const selectFilter = (state: AppState) => selectUsers(state).filter;

export const selectFilteredItems = createSelector([selectItems, selectFilter], (items, filter) => {
    let result = items;
    if (filter.login) {
        result = result.filter((item) => item.login.toLowerCase().includes(filter.login!.toLowerCase()));
    }
    if (filter.fullName) {
        result = result.filter((item) => item.fullName.toLowerCase().includes(filter.fullName!.toLowerCase()));
    }
    return result;
});

export const selectTotalCount = createSelector(selectFilteredItems, (items) => items.length);

const { reducer } = slice;
export default reducer;
